/**
 * Goal KIND identity — the one place that answers "what does a strategic goal
 * look like vs an agentic one?"
 *
 * The two kinds are the SAME object with the same states and the same progress
 * representation (see `goal-track.ts`); the only thing that differs is the
 * label they wear. That label is a glyph + an accent, and both live here so a
 * row, a section header, and a peek can never drift apart.
 *
 * On the accent: both tiles used to be `bg-primary/10 text-primary` — the same
 * near-invisible indigo wash, which made the one differentiating mark on the
 * row unreadable AND identical between kinds. Each kind now carries its own
 * hue, published as `--section` so the EXISTING tint/fg/border utilities paint
 * it (16% fill, full-hue glyph, 34% border) — no new palette, no new CSS. Both
 * hues are picked from `SECTION_SPECTRUM` and deliberately avoid the
 * success-green / warning-amber / danger-red the state dot already owns, so
 * "which kind" and "how healthy" never read as the same signal.
 */

import * as React from "react";
import type { CSSProperties } from "react";
import { cn } from "../../utils";
import { readableTextOn } from "../../tokens";
import { Bot, Target } from "../../icons";

export type GoalKind = "agentic" | "strategic";

export const GOAL_KIND_META: Record<
  GoalKind,
  { label: string; plural: string; accent: string; Glyph: typeof Target }
> = {
  strategic: {
    label: "Strategic",
    plural: "Strategic goals",
    // violet — a human-owned commitment. Distinct from the indigo primary, so
    // the tile reads as its own mark rather than as generic chrome.
    accent: "#8B5CF6",
    Glyph: Target,
  },
  agentic: {
    label: "Agentic",
    plural: "Agentic goals",
    // cyan — the fleet. Far enough from violet to tell apart at 12px.
    accent: "#06B6D4",
    Glyph: Bot,
  },
};

/** Publish a kind's accent as `--section`, so `.section-tint` / `.section-fg` /
 *  `.section-border` paint it — the same mechanism a `<SectionTag>` uses. */
export function goalKindVars(kind: GoalKind): CSSProperties {
  const accent = GOAL_KIND_META[kind].accent;
  return {
    ["--section" as string]: accent,
    ["--section-fg" as string]: readableTextOn(accent),
  } as CSSProperties;
}

/**
 * The row signifier — a tinted tile carrying the kind's glyph, where an issue
 * row carries a bare priority/status glyph. This is what makes a goal row
 * legible as a goal at a glance, and which KIND of goal at a second glance.
 */
export function GoalKindTile({
  kind,
  className,
}: {
  kind: GoalKind;
  className?: string;
}) {
  const { Glyph, label } = GOAL_KIND_META[kind];
  return (
    <span
      style={goalKindVars(kind)}
      title={`${label} goal`}
      className={cn(
        "section-tint section-fg inline-flex size-5 shrink-0 items-center justify-center rounded-sm",
        className,
      )}
      aria-hidden
    >
      <Glyph className="size-3" />
    </span>
  );
}
